/**
 * AgentSummaryCard - Single metric display for agent visualizations
 */

import React from 'react';
import { Card, CardContent } from '../../ui/card';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { SummaryCardVisualization, formatValue } from './types';

interface AgentSummaryCardProps {
  data: SummaryCardVisualization;
  className?: string;
}

export const AgentSummaryCard: React.FC<AgentSummaryCardProps> = ({ data, className }) => {
  const { title, value, format, trend } = data;

  const displayValue = typeof value === 'number' && format
    ? formatValue(value, format)
    : String(value);

  const getTrendIcon = () => {
    if (!trend) return null;

    switch (trend.direction) {
      case 'up':
        return <TrendingUp className="h-4 w-4" />;
      case 'down':
        return <TrendingDown className="h-4 w-4" />;
      case 'neutral':
      default:
        return <Minus className="h-4 w-4" />;
    }
  };

  const getTrendColor = () => {
    if (!trend) return '';

    switch (trend.direction) {
      case 'up':
        return 'text-green-600 dark:text-green-400';
      case 'down':
        return 'text-red-600 dark:text-red-400';
      case 'neutral':
      default:
        return 'text-muted-foreground';
    }
  };

  return (
    <Card className={cn("w-full max-w-xs", className)}>
      <CardContent className="p-4">
        <div className="space-y-1">
          <p className="text-sm text-muted-foreground">{title}</p>
          <p className="text-2xl font-bold text-foreground">{displayValue}</p>

          {trend && (
            <div className={cn("flex items-center gap-1 text-sm", getTrendColor())}>
              {getTrendIcon()}
              {trend.value && <span className="font-medium">{trend.value}</span>}
              {trend.label && (
                <span className="text-muted-foreground text-xs">{trend.label}</span>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
